/**
 * @file contacts-derived.ts
 * @description Виды контактов элемента «Контакты» секции, значение которых
 * берётся не из списка контактов, а из другого поля «Настроек сайта»
 * (phase19.28 этап А; решение пользователя 2026-10-24 по проекту
 * uiux-starter-site, вопрос 5: вид «Часы работы» из `workingHours`;
 * ЗоЗПП ст. 9 — режим работы продавца; SB118).
 *
 * Почему отдельный реестр, а не новая запись `SITE_CONTACT_KINDS`: список
 * контактов в настройках (`SiteDraft.contacts`) — это строки «вид + значение»,
 * которые владелец вводит сам. Часы работы уже задаются отдельным полем
 * `brand.workingHours`; если бы «Часы работы» стали видом списка, кабинет
 * предложил бы ввести их второй раз, и два значения разошлись бы. Поэтому
 * список контактов не меняется, а элемент «Контакты» умеет показывать больше
 * видов: `SECTION_CONTACT_KIND_KEYS` = виды списка + производные.
 *
 * OCP: новый производный вид — новая запись `SECTION_CONTACT_DERIVED_KINDS`
 * с полем-источником; витрина и кабинет читают реестр, а не ветвятся по виду.
 */

import { deepFreeze } from "./deep-freeze.js";
import { SITE_CONTACT_KIND_KEYS, SITE_CONTACT_KINDS, type SiteContactKind } from "./contacts.js";

/** Виды, значение которых берётся из отдельного поля настроек сайта. */
export const SECTION_CONTACT_DERIVED_KIND_KEYS = ["hours"] as const;
export type SectionContactDerivedKind = (typeof SECTION_CONTACT_DERIVED_KIND_KEYS)[number];

/** Все виды, которые можно отметить в элементе «Контакты» секции. */
export const SECTION_CONTACT_KIND_KEYS = [...SITE_CONTACT_KIND_KEYS, ...SECTION_CONTACT_DERIVED_KIND_KEYS] as const;
export type SectionContactKind = SiteContactKind | SectionContactDerivedKind;

/** Поле снимка настроек сайта (`snapshot.brand`), откуда берётся значение. */
export type SectionContactSource = "workingHours";

/** Описание производного вида: подпись, подсказка в кабинете и поле-источник. */
export interface SectionContactDerivedKindInfo {
  readonly kind: SectionContactDerivedKind;
  readonly label: string;
  /** Строка под переключателем в кабинете: откуда берётся значение. */
  readonly hint: string;
  readonly source: SectionContactSource;
}

export const SECTION_CONTACT_DERIVED_KINDS: Readonly<Record<SectionContactDerivedKind, SectionContactDerivedKindInfo>> =
  deepFreeze({
    hours: {
      kind: "hours",
      label: "Часы работы",
      hint: "Берётся из настроек сайта, поле «Часы работы»",
      source: "workingHours",
    },
  });

/** Сужение строки до производного вида. */
export function isSectionContactDerivedKind(kind: string): kind is SectionContactDerivedKind {
  return (SECTION_CONTACT_DERIVED_KIND_KEYS as readonly string[]).includes(kind);
}

/** Сужение строки до любого вида элемента «Контакты». */
export function isSectionContactKind(kind: string): kind is SectionContactKind {
  return (SECTION_CONTACT_KIND_KEYS as readonly string[]).includes(kind);
}

/** Подпись вида для кабинета и витрины — из нужного реестра. */
export function sectionContactKindLabel(kind: SectionContactKind): string {
  return isSectionContactDerivedKind(kind) ? SECTION_CONTACT_DERIVED_KINDS[kind].label : SITE_CONTACT_KINDS[kind].label;
}
